/**
 * Order Tracking Map - Real-time delivery tracking with OpenStreetMap
 * Shows driver location, delivery route, and order status
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ArrowLeft,
  Package,
  Truck,
  MapPin,
  CheckCircle,
  Clock,
  RefreshCw,
  Phone,
  Navigation,
  Building2,
  User,
  Copy
} from "lucide-react";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons
const driverIcon = new L.DivIcon({
  className: "driver-marker",
  html: "<div style=\"background: #00A86B; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);\"><svg width=\"20\" height=\"20\" viewBox=\"0 0 24 24\" fill=\"white\"><path d=\"M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z\"/></svg></div>",
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

const destinationIcon = new L.DivIcon({
  className: "destination-marker",
  html: "<div style=\"background: #EF4444; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);\"><svg width=\"18\" height=\"18\" viewBox=\"0 0 24 24\" fill=\"white\"><path d=\"M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z\"/></svg></div>",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const pudoIcon = new L.DivIcon({
  className: "pudo-marker",
  html: "<div style=\"background: #3B82F6; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);\"><svg width=\"18\" height=\"18\" viewBox=\"0 0 24 24\" fill=\"white\"><path d=\"M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z\"/></svg></div>",
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

// Map center updater component
function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

const statusSteps = [
  { key: "pending", label: "Order Placed", icon: Package },
  { key: "processing", label: "Processing", icon: Clock },
  { key: "packed", label: "Packed", icon: Package },
  { key: "in_transit", label: "In Transit", icon: Truck },
  { key: "at_pudo", label: "At PUDO", icon: Building2 },
  { key: "delivered", label: "Delivered", icon: CheckCircle },
];

const OrderTrackingMap = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [order, setOrder] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [eta, setEta] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);

  // Jamaica center coordinates
  const defaultCenter = [18.1096, -77.2975];

  const fetchOrderData = useCallback(async () => {
    try {
      const response = await api.get("/fulfillment/orders/" + orderId);
      setOrder(response.data);

      // If order has driver assigned, fetch driver location
      if (response.data?.fulfillment?.assigned_driver_id) {
        try {
          const driverRes = await api.get("/tracking/driver/" + response.data.fulfillment.assigned_driver_id);
          if (driverRes.data?.location) {
            setDriverLocation(driverRes.data.location);
            setEta(driverRes.data.eta);
            // Also set driver info for display
            if (driverRes.data.driver) {
              setDriverInfo(driverRes.data.driver);
            }
          }
        } catch (e) {
          // Driver location not available yet
        }
      }
    } catch (error) {
      console.error("Failed to fetch order:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderData();
    // Poll for updates every 10 seconds when in transit
    const interval = setInterval(() => {
      if (order?.fulfillment_status === "in_transit" || order?.fulfillment_status === "picked_up") {
        fetchOrderData();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchOrderData, order?.fulfillment_status]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrderData();
  };

  const copyTrackingCode = () => {
    if (order?.tracking_code) {
      navigator.clipboard.writeText(order.tracking_code);
      toast.success("Tracking code copied!");
    }
  };

  const getCurrentStepIndex = () => {
    const status = order?.fulfillment_status || "pending";
    return statusSteps.findIndex(s => s.key === status || s.key === status.replace("_", ""));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <Package className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Order Not Found</h2>
        <p className="text-gray-500 mb-4">The order you are looking for does not exist.</p>
        <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
      </div>
    );
  }

  const deliveryAddress = order.delivery_address || {};
  const destinationCoords = deliveryAddress.coordinates 
    ? [deliveryAddress.coordinates.lat, deliveryAddress.coordinates.lng]
    : null;
  
  const pudoCoords = order.fulfillment?.pudo_location 
    ? [order.fulfillment.pudo_location.lat, order.fulfillment.pudo_location.lng]
    : null;

  const mapCenter = driverLocation 
    ? [driverLocation.lat, driverLocation.lng]
    : destinationCoords || pudoCoords || defaultCenter;

  const currentStep = getCurrentStepIndex();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="order-tracking-map">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="p-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800">Track Order</h1>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{order.tracking_code || "N/A"}</span>
                {order.tracking_code && (
                  <button onClick={copyTrackingCode} className="text-gray-400 hover:text-gray-600">
                    <Copy className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={"h-5 w-5 text-gray-600 " + (refreshing ? "animate-spin" : "")} />
          </Button>
        </div>
      </header>

      {/* Map Section */}
      <div className="h-64 md:h-80 relative z-10">
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater center={mapCenter} />

          {/* Driver Marker */}
          {driverLocation && (
            <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
              <Popup>
                <div className="text-center">
                  <strong>Driver Location</strong>
                  <br />
                  {eta && <span className="text-sm text-gray-500">ETA: {eta} mins</span>}
                </div>
              </Popup>
            </Marker>
          )}

          {/* PUDO Marker */}
          {pudoCoords && order.fulfillment_status === "at_pudo" && (
            <Marker position={pudoCoords} icon={pudoIcon}>
              <Popup>
                <div className="text-center">
                  <strong>{order.fulfillment?.pudo_name || "PUDO Center"}</strong>
                  <br />
                  <span className="text-sm text-emerald-600">Ready for Pickup</span>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Destination Marker */}
          {destinationCoords && (
            <Marker position={destinationCoords} icon={destinationIcon}>
              <Popup>
                <div className="text-center">
                  <strong>Delivery Address</strong>
                  <br />
                  <span className="text-sm">{deliveryAddress.street}</span>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Route line from driver to destination */}
          {driverLocation && destinationCoords && (
            <Polyline
              positions={[[driverLocation.lat, driverLocation.lng], destinationCoords]}
              color="#00A86B"
              weight={4}
              opacity={0.7}
              dashArray="10, 10"
            />
          )}
        </MapContainer>

        {/* Driver Info Card */}
        {driverInfo && (
          <div className="absolute bottom-4 left-4 right-4 bg-slate-900/95 backdrop-blur-sm rounded-xl p-4 border border-slate-700 shadow-xl z-[1000]">
            <div className="flex items-center gap-4">
              {driverInfo.profile_photo_url ? (
                <img
                  src={driverInfo.profile_photo_url}
                  alt={driverInfo.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-green-500"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">{driverInfo.name}</span>
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">Your Driver</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
                  {driverInfo.vehicle_type && <span>{driverInfo.vehicle_type}</span>}
                  {driverInfo.rating && <span>⭐ {driverInfo.rating.toFixed(1)}</span>}
                </div>
                {eta && (
                  <div className="mt-2 text-amber-400 font-medium">
                    ETA: ~{eta} minutes
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ETA Overlay */}
        {eta && (order.fulfillment_status === "in_transit" || order.fulfillment_status === "picked_up") && (
          <div className="absolute top-4 right-4 bg-white rounded-xl px-4 py-2 shadow-lg z-20">
            <div className="flex items-center gap-2">
              <Navigation className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium text-gray-800">ETA: {eta} mins</span>
            </div>
          </div>
        )}
      </div>

      {/* Status Progress */}
      <div className="bg-white px-4 py-6 border-b">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-bold text-gray-800 mb-4">Delivery Status</h2>
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200">
              <div 
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: currentStep >= 0 ? ((currentStep / (statusSteps.length - 1)) * 100) + "%" : "0%" }}
              />
            </div>
            
            {/* Steps */}
            <div className="flex justify-between relative">
              {statusSteps.map((step, index) => {
                const StepIcon = step.icon;
                const isCompleted = index <= currentStep;
                const isCurrent = index === currentStep;
                
                return (
                  <div key={step.key} className="flex flex-col items-center">
                    <div className={
                      "w-8 h-8 rounded-full flex items-center justify-center z-10 transition-colors " +
                      (isCompleted ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-400") +
                      (isCurrent ? " ring-4 ring-emerald-100" : "")
                    }>
                      <StepIcon className="h-4 w-4" />
                    </div>
                    <span className={"text-xs mt-2 text-center " + (isCompleted ? "text-emerald-600 font-medium" : "text-gray-400")}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="p-4 max-w-4xl mx-auto space-y-4">
        {/* Driver Info (if assigned) */}
        {order.fulfillment?.assigned_driver && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Truck className="h-5 w-5 text-emerald-500" />
              Your Driver
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">{order.fulfillment.assigned_driver.name || "Driver"}</p>
                  <p className="text-sm text-gray-500">On the way</p>
                </div>
              </div>
              {order.fulfillment.assigned_driver.phone && (
                <a 
                  href={"tel:" + order.fulfillment.assigned_driver.phone}
                  className="p-3 bg-emerald-100 rounded-full hover:bg-emerald-200 transition-colors"
                >
                  <Phone className="h-5 w-5 text-emerald-600" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Delivery Address */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-red-500" />
            Delivery Address
          </h3>
          <p className="text-gray-700">
            {deliveryAddress.street || "Address not specified"}
            {deliveryAddress.city && ", " + deliveryAddress.city}
            {deliveryAddress.parish && ", " + deliveryAddress.parish}
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-500" />
            Order Summary
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Order ID</span>
              <span className="font-medium text-gray-800">#{order.id?.slice(0, 8)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Items</span>
              <span className="font-medium text-gray-800">{order.items?.length || 0} items</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total</span>
              <span className="font-bold text-emerald-600">J${order.total_amount?.toLocaleString() || 0}</span>
            </div>
          </div>
        </div>

        {/* Scan History */}
        {order.scan_history && order.scan_history.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-gray-800 mb-3">Tracking History</h3>
            <div className="space-y-3">
              {order.scan_history.slice(0, 5).map((scan, idx) => (
                <div key={scan.id || idx} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">
                      {scan.scan_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(scan.timestamp).toLocaleString()}
                      {scan.scanned_by_name && " by " + scan.scanned_by_name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTrackingMap;
